import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { DisplayColor, SubjectDto } from "@mmm/contracts";
import { SubjectDialog } from "./SubjectDialog.js";
import { createSubject, updateSubject, archiveSubject, activateSubject, deleteSubject } from "../../api/commands.js";
import { useConfirm } from "../shell/ConfirmProvider.js";
import { useT } from "@mmm/ui";
interface OpenCreateOptions {
  navigateOnCreate?: boolean;
}

type Request =
  | { mode: "create"; navigateOnCreate: boolean; subject?: undefined }
  | { mode: "edit"; subject: SubjectDto; navigateOnCreate?: undefined };

interface SubjectDialogContextValue {
  openCreate: (options?: OpenCreateOptions) => void;
  openEdit: (subject: SubjectDto) => void;
}

const SubjectDialogContext = createContext<SubjectDialogContextValue | null>(null);

export function SubjectDialogProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const confirm = useConfirm();
  const t = useT();
  const [request, setRequest] = useState<Request | null>(null);

  const openCreate = useCallback(({ navigateOnCreate = true }: OpenCreateOptions = {}) => {
    setRequest({ mode: "create", navigateOnCreate });
  }, []);

  const openEdit = useCallback((subject: SubjectDto) => {
    setRequest({ mode: "edit", subject });
  }, []);

  const close = () => setRequest(null);

  const handleSubmit = async ({ title, color }: { title: string; color: DisplayColor }) => {
    if (!request) return;

    if (request.mode === "edit") {
      await updateSubject({ subjectId: request.subject.id, title, color });
      close();
      return;
    }

    const subject = await createSubject({ title, color });
    close();
    if (request.navigateOnCreate) navigate(`/subjects/${subject.id}`);
  };

  const handleArchiveToggle = async () => {
    if (request?.mode !== "edit") return;
    const { id, status } = request.subject;
    // SubjectDialog's Button invokes this without awaiting it — a failure is
    // already toasted by commands.ts's run(); this catch just keeps the
    // rejection from surfacing as unhandled, and (since it's inside the
    // await) also keeps the dialog open instead of wrongly closing.
    try {
      if (status === "active") await archiveSubject({ subjectId: id });
      else await activateSubject({ subjectId: id });
      close();
    } catch {
      // Nothing left to do.
    }
  };

  const handleDelete = async () => {
    if (request?.mode !== "edit") return;
    const { id, title } = request.subject;
    const ok = await confirm({
      title: t("subjects.deleteSubject"),
      message: t("subjects.deleteConfirm", { title }),
      confirmLabel: t("subjects.deleteSubject"),
    });
    if (!ok) return;
    // SubjectDialog's Button invokes this without awaiting it — a failure is
    // already toasted; keep the dialog open (don't close/navigate) instead of
    // letting the rejection escape unhandled.
    try {
      await deleteSubject({ subjectId: id });
      close();
      // If the deleted subject's workspace is open, leave it.
      if (location.pathname.startsWith(`/subjects/${id}`)) navigate("/subjects");
    } catch {
      // Nothing left to do.
    }
  };

  return (
    <SubjectDialogContext.Provider value={{ openCreate, openEdit }}>
      {children}
      {request && (
        <SubjectDialog
          mode={request.mode}
          initialTitle={request.subject?.title}
          initialColor={request.subject?.color}
          archived={request.subject?.status === "archived"}
          onArchiveToggle={handleArchiveToggle}
          onDelete={handleDelete}
          onSubmit={handleSubmit}
          onClose={close}
        />
      )}
    </SubjectDialogContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSubjectDialog(): SubjectDialogContextValue {
  const context = useContext(SubjectDialogContext);
  if (!context) throw new Error("useSubjectDialog must be used inside SubjectDialogProvider");
  return context;
}
